// ═══════════════════════════════════════════════════════════════
// AdminService — Appels API pour le dashboard admin
//
// Routes utilisées :
//   - GET  /admin/is-admin
//   - GET  /admin/stats?days=30
//   - POST /admin/withdrawals/:id/mark-paid
//   - POST /admin/withdrawals/:id/reject
// ═══════════════════════════════════════════════════════════════

import { AWS_CONFIG } from '../config/aws_config';

const API = AWS_CONFIG.apiUrl;

async function authedFetch(user, path, options = {}) {
  if (!user?.accessToken) throw new Error('not_authenticated');
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer ' + user.accessToken,
    ...(options.headers || {}),
  };
  const res = await fetch(`${API}${path}`, { ...options, headers });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data?.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.body = data;
    throw err;
  }
  return data;
}

/**
 * Vérifiez si le user connecté est admin (hardcoded ou flag DB)
 */
export async function checkIsAdmin(user) {
  try {
    const data = await authedFetch(user, '/admin/is-admin');
    return data?.is_admin === true;
  } catch {
    return false;
  }
}

/**
 * Récupère les stats globales du dashboard
 * @param {number|string} days - 7 | 30 | 90 | 'all'
 */
export async function getAdminStats(user, days = 30) {
  return await authedFetch(user, `/admin/stats?days=${days}`);
}

/**
 * Récupère les clics CJ détaillés pour le dashboard admin
 * @param {Object} opts - { days: 7, partner: 'iqueens' | 'cashmere' | null, limit: 100 }
 * @returns {Promise<{stats_by_partner, stats_by_day, top_products, recent_clicks, total_recent}>}
 */
export async function getAdminClicks(user, opts = {}) {
  const params = new URLSearchParams();
  params.set('days',  String(opts.days  || 7));
  params.set('limit', String(opts.limit || 100));
  if (opts.partner) params.set('partner', opts.partner);
  return await authedFetch(user, `/admin/clicks?${params.toString()}`);
}

/**
 * Trigger manuel de la synchronisation des clics CJ.
 * Recalcule user_wallet.total_clicks et fait un health check du matching.
 * @returns {Promise<{success, counts, matching, wallets_recalculated}>}
 */
export async function syncAdminClicks(user) {
  return await authedFetch(user, '/admin/sync-clicks', { method: 'POST' });
}

/**
 * Marque un retrait comme payé (après transfert PayPal)
 */
export async function markWithdrawalPaid(user, withdrawalId, opts = {}) {
  return await authedFetch(user, `/admin/withdrawals/${withdrawalId}/mark-paid`, {
    method: 'POST',
    body: JSON.stringify({
      transaction_ref: opts.transactionRef || null,
      note: opts.note || null,
    }),
  });
}

/**
 * Refuse un retrait + rollback wallet
 */
export async function rejectWithdrawal(user, withdrawalId, note) {
  return await authedFetch(user, `/admin/withdrawals/${withdrawalId}/reject`, {
    method: 'POST',
    body: JSON.stringify({ note: note || null }),
  });
}

/**
 * Déclenche manuellement le sync CJ (au lieu d'attendre les 3h CloudWatch)
 * Utilise la route publique /sync-commissions avec le secret.
 * @param {string} syncSecret - le SYNC_SECRET configuré en env Lambda
 */
export async function triggerCJSync(syncSecret) {
  const url = `${API}/sync-commissions?secret=${encodeURIComponent(syncSecret)}`;
  const res = await fetch(url);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
  return data;
}

/**
 * Récupère les stats push (users avec notif activée)
 */
export async function getPushStats(user) {
  return await authedFetch(user, '/admin/push/stats');
}

/**
 * Envoie une notification test à soi-même (ou un user spécifique)
 */
export async function sendTestPush(user, opts = {}) {
  return await authedFetch(user, '/admin/push/test', {
    method: 'POST',
    body: JSON.stringify({
      target_user_id: opts.targetUserId || null,
      title: opts.title || null,
      body: opts.body || null,
      screen: opts.screen || null,
    }),
  });
}

/**
 * Broadcast à un segment (all, free, basic, premium, gold)
 */
export async function broadcastPush(user, segment, title, body, screen) {
  return await authedFetch(user, '/admin/push/broadcast', {
    method: 'POST',
    body: JSON.stringify({ segment, title, body, screen: screen || null }),
  });
}
